<template>
    <div v-if="reports.length !== 0" class="row items-center justify-between">
        <div class="text-secondary q-my-md">
            {{ reportViewDescription[selectedView] }}
        </div>
        <q-btn-group flat square v-if="!$q.platform.is.mobile">
            <q-btn
                id="download-csv-button"
                @click="exportDataAsCSV"
                :loading="isDownloadingCSV"
                icon="far fa-file-csv"
                color="primary"
                class="q-mr-sm"
                outline
                :label="$t('Export all data (CSV)')"
            >
                <q-tooltip style="font-size: 14px">
                    {{ $t('Export all data (CSV)') }}
                </q-tooltip>
            </q-btn>
            <q-btn
                id="download-xls-button"
                @click="exportDataAsXLS"
                :loading="isDownloadingXLS"
                icon="far fa-file-excel"
                color="primary"
                outline
                :label="$t('Export all data (XLS)')"
            >
                <q-tooltip style="font-size: 14px">
                    {{ $t('Export all data (XLS)') }}
                </q-tooltip>
            </q-btn>
        </q-btn-group>
    </div>
    <div
        v-if="reports.length === 0"
        class="q-mt-xl bg-white q-pa-md border-grey"
    >
        <q-icon name="fal fa-exclamation-triangle" size="md" />
        <span style="margin-left: 10px">
            {{ $t('No records found for this date range.') }}
        </span>
    </div>
    <div v-else class="full-height">
        <div
            v-for="report in filteredReports"
            :key="report.assetId"
            class="q-my-lg"
        >
            <div class="row justify-between bg-primary q-px-md q-py-sm">
                <div>
                    <span class="q-ml-sm text-h5 text-weight-medium text-white">
                        {{ report.assetName }}
                    </span>
                </div>
            </div>

            <!-- Product View -->
            <changeover-product-table
                v-if="selectedView === 'products'"
                :report="report.productView"
                :facilityId="facilityId"
                :pagination="changeoverPagination"
                @update:pagination="changeoverPagination = $event"
            />

            <!-- Attribute View -->
            <changeover-property-table
                v-else-if="selectedView === 'properties'"
                :report="report.propertyView"
                :facilityId="facilityId"
                :pagination="changeoverPagination"
                @update:pagination="changeoverPagination = $event"
            />

            <!-- Cause View -->
            <changeover-cause-table
                v-else
                :report="report.causeView"
                :facilityId="facilityId"
                :pagination="changeoverPagination"
                @update:pagination="changeoverPagination = $event"
            />
        </div>
    </div>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import ChangeoverCauseTable from './ChangeoverCauseTable.vue';
import ChangeoverProductTable from './ChangeoverProductTable.vue';
import ChangeoverPropertyTable from './ChangeoverPropertyTable.vue';
import { buildChangeoverReport } from './changeoverReport.js';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useReportsStore } from '@stores/reports.ts';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ChangeoverReport',
    components: {
        ChangeoverPropertyTable,
        ChangeoverProductTable,
        ChangeoverCauseTable
    },
    props: {
        records: {
            required: true,
            validator(val) {
                return Array.isArray(val) || val === null;
            }
        },
        facilityId: {
            type: Number,
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        selectedView: {
            type: String,
            default: 'product'
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    watch: {
        /**
         * Reset pagination when selected view changes
         */
        selectedView() {
            this.changeoverPagination = {
                sortBy: '',
                descending: false,
                rowsPerPage: 0 // infinite
            };
        }
    },
    data() {
        return {
            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // file exporter class
            fileExporter: null,

            reportViewDescription: {
                products: this.$t(
                    'Changeovers grouped by transitions between products.'
                ),
                properties: this.$t(
                    'Changeovers grouped by transitions between product properties.'
                ),
                causes: this.$t('Changeovers grouped by the changeover cause.')
            },
            changeoverPagination: {
                sortBy: '',
                descending: false,
                rowsPerPage: 0 // infinite
            }
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange',
            selectedTimezone: 'selectedTimezone'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Build report data of current interval
         * @return {Object} report data with product, property and cause views
         */
        reports() {
            return buildChangeoverReport(this.records);
        },
        filteredReports() {
            return this.reports
                .filter(report => {
                    return this.filters.assetIds.has(report.assetId);
                })
                .sort((a, b) => {
                    return a.assetName.localeCompare(b.assetName);
                });
        },
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for exporting
         */
        fileHeaders() {
            return [
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('Changeover Start Date').concat(
                    ` [${this.selectedTimezone}]`
                ),
                this.$t('Changeover End Date').concat(
                    ` [${this.selectedTimezone}]`
                ),
                this.$t('Changeover Cause'),
                this.$t('Cause Code'),
                this.$t('Changeover Duration [s]'),
                this.$t('Expected Duration [s]'),
                this.$t('Exceeded Duration [s]'),
                this.$t('Next Product'),
                this.$t('Next SKU'),
                this.$t('Next Product Category'),
                this.$t('Next Product Properties'),
                this.$t('Previous Product'),
                this.$t('Previous SKU'),
                this.$t('Previous Product Category'),
                this.$t('Previous Product Properties'),
                this.$t('Report Start Date').concat(
                    ` [${this.selectedTimezone}]`
                ),
                this.$t('Report End Date').concat(` [${this.selectedTimezone}]`)
            ];
        },
        /**
         * Get the file rows for file exporting
         * @return {Array} file rows for exporting
         */
        fileRows() {
            let rows = [];

            const filteredRecords = this.records.filter(record => {
                return this.filters.assetIds.has(record['asset_id']);
            });

            for (const record of filteredRecords) {
                const row = [
                    record['facility_name'],
                    record['asset_name'],
                    this.formatDate(record['start_date']),
                    this.formatDate(record['end_date']),
                    record['changeover_cause'],
                    record['cause_code'],
                    record['changeover_duration'],
                    record['expected_duration'],
                    record['exceeded_duration'],
                    record['starting_product'],
                    record['starting_sku'],
                    record['starting_product_category'],
                    this.getPropertiesString(
                        record['starting_product_properties']
                    ),
                    record['ending_product'],
                    record['ending_sku'],
                    record['ending_product_category'],
                    this.getPropertiesString(
                        record['ending_product_properties']
                    ),
                    this.formatDate(record['start']),
                    this.formatDate(record['end'])
                ];
                rows.push(row);
            }

            return rows;
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId)?.name;
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Export raw records as CSV.
         */
        async exportDataAsCSV(assetId) {
            // begin loading
            this.isDownloadingCSV = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                await this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_changeover_${start_date}_${end_date}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS(assetId) {
            // begin loading
            this.isDownloadingXLS = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_changeover_${start_date}_${end_date}`,
                    sheetName: this.$t('Changeover Report'),
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                console.error(error);
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        },
        /**
         * Constructs a string representation of the given properties object.
         * @param {Object} properties - The properties object to be converted to a string.
         * @returns {string} - The string representation of the properties object.
         */
        getPropertiesString(properties) {
            // If properties is null, undefined, or an empty object, return an empty string
            if (!properties || Object.keys(properties).length === 0) {
                return '';
            }

            // Convert the properties object to a JSON string
            let string = JSON.stringify(properties);

            return string.replace(/"/g, '""');
        }
    }
};
</script>

<style lang="scss" scoped>
$table-height: 100%;
$report-header-panel-height: 50px;

.toggle-report-view-button :deep(.q-btn__wrapper) {
    padding: 6px;
}

:deep(.report-container) {
    border: 1px solid grey;
    height: calc(#{$table-height} - #{$report-header-panel-height});
}

.report-panel-header {
    height: $report-header-panel-height;
}
</style>
